import ky, { type AfterResponseHook, type BeforeRequestHook, type KyInstance } from 'ky';
import { Injectable } from '../../decorators/injectable';
import { notifyConsultantNotActivePartnerHook } from './consultant-not-active-partner';
import { type BaseKyConfigParams, buildBaseKyConfig } from './ky-base-config';

export interface IKyClientService {
  getClient(): KyInstance;
  /**
   * Builds a ky client that reuses the shared base config (prefix, no retry,
   * Accept-Language, JSON error-body extraction) but with caller-supplied timeout
   * and auth hooks. Used by the unauthenticated inspection portal, which swaps the
   * Clerk auth hook for an `x-inspection-token` hook.
   */
  createClient(params: BaseKyConfigParams): KyInstance;
}

const clerkAuthHook: BeforeRequestHook = async ({ request }) => {
  const clerk = (
    window as Window & {
      Clerk?: { session?: { getToken: () => Promise<string | null> }; loaded?: boolean };
    }
  ).Clerk;
  if (clerk?.loaded === false) {
    await new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 100;
      const checkLoaded = setInterval(() => {
        attempts++;
        if (clerk.loaded !== false || attempts >= maxAttempts) {
          clearInterval(checkLoaded);
          resolve(undefined);
        }
      }, 50);
    });
  }
  const token = await clerk?.session?.getToken();
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }

  const tenantContext = localStorage.getItem('tenant_context');
  if (tenantContext) {
    try {
      const parsed = JSON.parse(tenantContext);
      if (parsed?.currentTenantId) {
        request.headers.set('x-tenant-id', parsed.currentTenantId);
      }
    } catch {
      // ignore malformed localStorage
    }
  }
};

const signOutOnUnauthorizedHook: AfterResponseHook = async ({ request, response, retryCount }) => {
  if (response.status !== 401) {
    return response;
  }
  // Only sign out when the request actually carried a token that the server
  // rejected (real session-expired case). If no Authorization header was
  // attached (e.g. Clerk session not yet initialised right after sign-up
  // redirect), do NOT sign out — the user is still authenticated in Clerk
  // and the session will be ready on the next request.
  const hadToken = !!request.headers.get('Authorization');
  if (hadToken) {
    // Long-running uploads can outlive the ~60s Clerk JWT: a 401 mid-flight
    // doesn't mean the session is dead, just that the token attached at
    // beforeRequest time went stale. Retry once with a fresh token before
    // signing the user out. `retryCount === 0` is ky's own per-request retry
    // counter (bumped internally after our forced retry), so it doubles as
    // the anti-loop guard — a second 401 (retryCount === 1) falls straight
    // through to signOut, no separate marker needed.
    if (retryCount === 0) {
      const clerk = (
        window as Window & {
          Clerk?: {
            session?: { getToken: (opts?: { skipCache?: boolean }) => Promise<string | null> };
          };
        }
      ).Clerk;
      const freshToken = await clerk?.session?.getToken({ skipCache: true });
      if (freshToken) {
        const headers = new Headers(request.headers);
        headers.set('Authorization', `Bearer ${freshToken}`);
        // `request` here is ky's pre-retry clone (made before the original fetch
        // fired, see ky's `#fetch()`), so its body stream is still unconsumed —
        // this also works for multipart/FormData uploads, verified empirically.
        return ky.retry({ request: new Request(request, { headers }), delay: 0 });
      }
    }
    await (
      window as Window & {
        Clerk?: { signOut: () => Promise<void> };
      }
    ).Clerk?.signOut();
  }
  return response;
};

@Injectable()
class KyClientService implements IKyClientService {
  private readonly instance: KyInstance;

  constructor() {
    const base = buildBaseKyConfig({ timeout: 30_000, authHooks: [clerkAuthHook] });
    this.instance = ky.create({
      ...base,
      // retry.limit must be >0 for the 401-retry-with-fresh-token hook above: ky only
      // pre-clones the request (keeping an unconsumed body for multipart uploads) when
      // retry is enabled. `methods: []` keeps ky's own automatic retries disabled — only
      // our explicit `ky.retry()` call can trigger the one allowed retry.
      retry: { limit: 1, methods: [] },
      hooks: {
        ...base.hooks,
        afterResponse: [signOutOnUnauthorizedHook, notifyConsultantNotActivePartnerHook],
      },
    });
  }

  getClient(): KyInstance {
    return this.instance;
  }

  createClient(params: BaseKyConfigParams): KyInstance {
    return ky.create(buildBaseKyConfig(params));
  }
}

export { KyClientService };
